import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Editor as TinyMCEEditor } from 'tinymce';
import type {
  SuggestionAcceptedEvent,
  SuggestionRejectedEvent,
  SuggestionRequestEvent,
  SuggestionsApi,
} from 'wfp-tinymce/js/tinymce/plugins/suggestions/plugin';

import { DATA_KEY_ON_DATA_SET } from 'src/components/ReportTemplate/Workspace/config';
import useContextualActionsHint from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/useContextualActionsHint';
import { useKeepInView } from 'src/hooks';
import {
  acceptPreview,
  clearPreview,
  generateDraftRequest,
  rejectPreview,
  rephraseRequest,
} from 'src/redux/aiNarrative/actions';
import { selectAiPreview } from 'src/redux/aiNarrative/reducers';
import { AiActionKind, AiNarrativeContext } from 'src/redux/aiNarrative/types';
import {
  fetchTemplatesRequest,
  resetTemplates,
} from 'src/redux/aiPromptTemplates/actions';
import { selectPromptTemplates } from 'src/redux/aiPromptTemplates/reducers';
import { selectAiQuota } from 'src/redux/aiQuota/reducers';
import {
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import { RootState } from 'src/redux/store';
import type { INarrative } from 'src/types';
import { sendGAEvent } from 'src/utils';
import { stripCitations } from 'src/utils/aiMarkdown';
import {
  buildSourceData,
  quotaStatus,
  readSectionNames,
} from 'src/utils/aiNarrative';
import { readTemplateActions } from 'src/utils/aiResponses';

interface Options {
  narrative: INarrative;
  isEdit: boolean;
  notPublished: boolean;
  isFetching: boolean;
  content: string;
  setContent: Dispatch<SetStateAction<string>>;
  setAutosave: Dispatch<SetStateAction<boolean>>;
  tinyEditor: TinyMCEEditor | null;
}

const getSuggestionsApi = (
  editor: TinyMCEEditor | null,
): SuggestionsApi | null =>
  (editor?.plugins?.suggestions as unknown as SuggestionsApi) || null;

// Wires a narrative to the AI assist: draft generation, the rephrase
// suggestions inside the editor, quota and prompt templates.
export default function useAiNarrative({
  narrative,
  isEdit,
  notPublished,
  isFetching,
  content,
  setContent,
  setAutosave,
  tinyEditor,
}: Options) {
  const { id, title, limit, narrative_type: narrativeType } = narrative;

  const dispatch = useDispatch();
  const pendingRequestIdRef = React.useRef<string | null>(null);

  const activeSectionID = useSelector(
    (state: RootState) => state.viewState.activeSectionID,
  );
  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY].data,
  );
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const aiPreview = useSelector(selectAiPreview(id));

  // Latched: the type of a narrative is structural and cannot change while it is
  // being edited, so a save response that comes back without it must not switch
  // the AI actions off.
  const [aiNarrativeType, setAiNarrativeType] = useState(narrativeType || '');

  useEffect(() => {
    if (narrativeType && narrativeType !== aiNarrativeType) {
      setAiNarrativeType(narrativeType);
    }
  }, [narrativeType, aiNarrativeType]);

  const aiStatus = aiPreview?.status ?? 'idle';
  const aiMode = aiPreview?.mode ?? null;
  const draftLoading = aiMode === 'draft' && aiStatus === 'loading';
  const draftReady = aiMode === 'draft' && aiStatus === 'ready';
  const rephraseLoading = aiMode === 'rephrase' && aiStatus === 'loading';
  const rephraseReady = aiMode === 'rephrase' && aiStatus === 'ready';
  const busy = aiStatus === 'loading' || aiStatus === 'ready';
  const busyRef = React.useRef(busy);
  busyRef.current = busy;

  const narrativeBusy = busy || isFetching;

  const { ref: editorAreaRef, arm: keepEditorInView } =
    useKeepInView<HTMLDivElement>(draftReady);

  const aiContext: AiNarrativeContext = useMemo(() => {
    const { sectionName, subsectionName } = readSectionNames(
      reportDetailData?.sections,
      activeSectionID,
    );

    return {
      narrativeType: aiNarrativeType,
      sourceData: buildSourceData(sectionDetailData, DATA_KEY_ON_DATA_SET),
      cspCode: reportDetailData?.project_code,
      countryCode: reportDetailData?.country_iso_2,
      countryName: reportDetailData?.country,
      year: reportDetailData?.period,
      sectionName: sectionName || sectionDetailData?.current_title,
      subsectionName,
      narrativeName: title,
      wordCountLimit: limit,
    };
  }, [
    aiNarrativeType,
    sectionDetailData,
    reportDetailData?.sections,
    reportDetailData?.project_code,
    reportDetailData?.country_iso_2,
    reportDetailData?.country,
    reportDetailData?.period,
    activeSectionID,
    title,
    limit,
  ]);

  const canUseAi =
    reportDetailData?.permissions?.has_permission_to_use_ai === true;

  const quota = useSelector(selectAiQuota);
  const {
    remaining: quotaRemaining,
    exhausted: quotaExhausted,
    low: quotaLow,
  } = useMemo(() => quotaStatus(quota), [quota]);

  const promptTemplates = useSelector(selectPromptTemplates(aiNarrativeType));
  const { primary: primaryActions, contextual: contextualActions } = useMemo(
    () =>
      canUseAi
        ? readTemplateActions(promptTemplates.items)
        : { primary: [], contextual: [] },
    [canUseAi, promptTemplates.items],
  );

  const contextualActionsAvailable =
    isEdit && notPublished && contextualActions.length > 0;

  const hint = useContextualActionsHint(contextualActionsAvailable);

  // Only from idle: retrying from the error state would loop through this effect.
  useEffect(() => {
    if (
      canUseAi &&
      isEdit &&
      promptTemplates.status === 'idle' &&
      aiNarrativeType
    ) {
      dispatch(
        fetchTemplatesRequest({
          key: aiNarrativeType,
          narrativeType: aiNarrativeType,
        }),
      );
    }
  }, [canUseAi, isEdit, promptTemplates.status, aiNarrativeType]);

  const requestContextRef = React.useRef({ content, aiContext });
  requestContextRef.current = { content, aiContext };

  useEffect(() => {
    if (!tinyEditor) return undefined;

    const onRequest = (e: SuggestionRequestEvent) => {
      pendingRequestIdRef.current = e.requestId;
      sendGAEvent('ai_action_btn_clicked', { wfp_ai_action: e.item.value });
      dispatch(
        rephraseRequest({
          narrativeId: id,
          requestId: e.requestId,
          selectedText: e.text,
          currentContent: requestContextRef.current.content,
          action: e.item.value,
          context: requestContextRef.current.aiContext,
        }),
      );
    };

    const onAccepted = (e: SuggestionAcceptedEvent) => {
      pendingRequestIdRef.current = null;
      sendGAEvent('ai_action_accepted', { wfp_ai_action: e.item.value });
      setContent(tinyEditor.getContent());
      setAutosave(true);
      dispatch(acceptPreview({ narrativeId: id }));
    };

    const onRejected = (e: SuggestionRejectedEvent) => {
      pendingRequestIdRef.current = null;
      if (e.reason === 'user') {
        sendGAEvent('ai_action_rejected', { wfp_ai_action: e.item.value });
      }
      dispatch(rejectPreview({ narrativeId: id }));
    };

    tinyEditor.on('SuggestionRequest', onRequest);
    tinyEditor.on('SuggestionAccepted', onAccepted);
    tinyEditor.on('SuggestionRejected', onRejected);

    return () => {
      tinyEditor.off('SuggestionRequest', onRequest);
      tinyEditor.off('SuggestionAccepted', onAccepted);
      tinyEditor.off('SuggestionRejected', onRejected);
    };
  }, [tinyEditor, id, dispatch]);

  useEffect(() => {
    const suggestions = getSuggestionsApi(tinyEditor);
    const rephrase = aiPreview?.rephrase;
    if (!suggestions || aiMode !== 'rephrase' || !rephrase?.requestId) return;

    if (aiStatus === 'ready' && rephrase.proposedText) {
      suggestions.showSuggestion(rephrase.requestId, rephrase.proposedText);
    }
  }, [tinyEditor, aiStatus, aiMode]);

  useEffect(() => {
    const suggestions = getSuggestionsApi(tinyEditor);
    if (!suggestions) return;

    const available =
      contextualActionsAvailable && !narrativeBusy && !quotaExhausted;

    suggestions.setItems(
      available
        ? contextualActions.map(({ action, label }) => ({
            value: action,
            label,
          }))
        : [],
    );
  }, [
    tinyEditor,
    contextualActions,
    contextualActionsAvailable,
    narrativeBusy,
    quotaExhausted,
  ]);

  useEffect(() => {
    if (aiStatus !== 'error') return;

    if (aiMode === 'rephrase' && pendingRequestIdRef.current) {
      getSuggestionsApi(tinyEditor)?.cancelSuggestion(
        pendingRequestIdRef.current,
      );
    }
    dispatch(clearPreview({ narrativeId: id }));
  }, [aiStatus]);

  useEffect(() => {
    if (!isEdit) {
      dispatch(clearPreview({ narrativeId: id }));
      if (promptTemplates.status === 'error') {
        dispatch(resetTemplates({ key: aiNarrativeType }));
      }
    }
  }, [isEdit]);

  useEffect(() => {
    return () => {
      dispatch(clearPreview({ narrativeId: id }));
    };
  }, []);

  function acceptDraft() {
    if (!aiPreview?.draftHtml) return;

    keepEditorInView();
    sendGAEvent('ai_action_accepted', { wfp_ai_action: aiPreview.action });
    setContent(stripCitations(aiPreview.draftHtml));
    setAutosave(true);
    dispatch(acceptPreview({ narrativeId: id }));
  }

  function rejectDraft() {
    keepEditorInView();
    sendGAEvent('ai_action_rejected', { wfp_ai_action: aiPreview?.action });
    dispatch(rejectPreview({ narrativeId: id }));
  }

  function generateDraft(action: AiActionKind) {
    sendGAEvent('ai_action_btn_clicked', { wfp_ai_action: action });
    dispatch(
      generateDraftRequest({
        narrativeId: id,
        currentContent: content,
        action,
        context: aiContext,
      }),
    );
  }

  return {
    canUseAi,
    preview: aiPreview,
    draftLoading,
    draftReady,
    rephraseLoading,
    rephraseReady,
    busy,
    busyRef,
    narrativeBusy,
    quota,
    quotaRemaining,
    quotaExhausted,
    quotaLow,
    primaryActions,
    hint,
    editorAreaRef,
    acceptDraft,
    rejectDraft,
    generateDraft,
  };
}
